import { useAppSelector } from 'app/hooks';
import FormInput from 'components/common/form-input';
import { AnySizingItemAttributeType, AnySizingItemType } from 'data/types';
import { getValidSizingAttributeOptions } from 'utils/sizing';
import { FieldConfig, SizingItemConfig } from 'utils/sizing-item-configs';

type SizingItemDefaultsFormProps = {
  config: SizingItemConfig;
  currentValues: Record<string, unknown>;
  onUpdate: (updates: Record<string, unknown>) => void;
};

export default function SizingItemDefaultsForm({
  config,
  currentValues,
  onUpdate
}: SizingItemDefaultsFormProps) {
  const { validSizingAttributes } = useAppSelector((state) => state.data);

  const hasValidOptions =
    validSizingAttributes[config.validAttributeKey]?.length > 0;

  if (!hasValidOptions) {
    return (
      <p className='text-sm text-gray-500'>
        No configuration options available.
      </p>
    );
  }

  const mockItem = config.getMockItem(currentValues) as AnySizingItemType;

  const getOptions = (field: FieldConfig) => {
    if (field.options === 'fromValidAttributes' && field.attributeKey) {
      return getValidSizingAttributeOptions(
        config.validAttributeKey,
        mockItem,
        field.attributeKey as AnySizingItemAttributeType
      );
    }
    return field.options as string[];
  };

  const handleFieldChange = (key: string, value: unknown) => {
    onUpdate({ [key]: value });
  };

  const renderField = (field: FieldConfig) => {
    if (
      field.conditionalRender &&
      !field.conditionalRender(currentValues as Record<string, unknown>)
    ) {
      return null;
    }

    if (field.type === 'boolean') {
      return (
        <FormInput
          key={field.key}
          value={currentValues[field.key] ? 'Yes' : 'No'}
          label={field.label}
          subLabel={field.subLabel}
          type='select'
          options={['Yes', 'No']}
          onChange={(value) => handleFieldChange(field.key, value === 'Yes')}
          required
          preventDeselection
        />
      );
    }

    if (field.type === 'number') {
      return (
        <FormInput
          key={field.key}
          value={currentValues[field.key] as number}
          label={field.label}
          subLabel={field.subLabel}
          type='number'
          suffix={field.suffix}
          min={field.min}
          onChange={(value) => handleFieldChange(field.key, value)}
          required
        />
      );
    }

    return (
      <FormInput
        key={field.key}
        value={currentValues[field.key] as string}
        label={field.label}
        subLabel={field.subLabel}
        type='select'
        options={getOptions(field)}
        onChange={(value) => handleFieldChange(field.key, value)}
        required
        preventDeselection
      />
    );
  };

  return (
    <div className='flex flex-wrap items-start gap-x-5 gap-y-4'>
      {config.fields.map((field) => renderField(field))}
    </div>
  );
}
